import { ExternalLink, Fuel, UtensilsCrossed, Zap } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { placeLinks } from "../utils/placeLink";
import type { Amenity, UiLang } from "../lib/types";
import { PlaceAltLink } from "./PlaceAltLink";

interface Props {
  amenities: Amenity[];
  lang: UiLang;
  /** Kompakt variant til tidslinjen; full variant til detaljpanelet. */
  compact?: boolean;
  /**
   * Stoppet fasilitetene ligger ved.
   *
   * Brukes bare i søkelenka, og det er der det trengs: «Bunnpris» alene finner
   * tre hundre butikker, «Bunnpris Eidfjord» finner den ene som ligger her.
   */
  near?: string;
}

const ICONS = { charging: Zap, fuel: Fuel, food: UtensilsCrossed } as const;

/**
 * Lade-, bensin- og spisesteder nær et stopp.
 *
 * Dataene er hentet fra OpenStreetMap på forhånd. Avstanden er luftlinje fra
 * stoppet, så den er et anslag — men den skiller «i sentrum» fra «ti minutter
 * unna», som er det man trenger å vite.
 */
export function AmenityList({ amenities, lang, compact = false, near }: Props) {
  const t = dictionaries[lang];
  if (amenities.length === 0) {
    return compact ? null : <p className="amenity-empty">{t.noAmenities}</p>;
  }

  const groups = (["charging", "fuel", "food"] as const)
    .map((kind) => ({ kind, items: amenities.filter((a) => a.kind === kind) }))
    .filter((group) => group.items.length > 0);

  if (compact) {
    return (
      <span className="amenity-chips">
        {groups.map(({ kind, items }) => {
          const Icon = ICONS[kind];
          const hasFast = kind === "charging" && items.some((a) => a.fastCharge);
          return (
            <span
              key={kind}
              className={`amenity-chip amenity-chip--${kind}`}
              title={items.map((a) => a.name).join(", ")}
            >
              <Icon size={11} />
              {items.length}
              {hasFast && <span className="amenity-fast">⚡</span>}
            </span>
          );
        })}
      </span>
    );
  }

  return (
    <div className="amenity-groups">
      {groups.map(({ kind, items }) => {
        const Icon = ICONS[kind];
        return (
          <div className="amenity-group" key={kind}>
            <div className="amenity-heading">
              <Icon size={13} />
              {t[kind]}
            </div>
            <ul className="amenity-items">
              {items.map((item) => {
                const links = placeLinks(item, near);
                return (
                  <li className="amenity-item" key={item.id}>
                    {/*
                      Navnet er en lenke, alltid. Ingen av de 1 534 oppføringene
                      fra OpenStreetMap har en nettside, så et kartsøk på navn og
                      punkt er det beste svaret som finnes — og det svarer på det
                      man faktisk lurer på: hvor er det, er det åpent, passer det
                      bilen. `title` sier hva stedet er, for den som bare holder
                      musepekeren over.

                      Reservelenka står bare der en oppføring faktisk har en
                      nettside. I dagens datasett gjør ingen av dem det, så den
                      vises aldri her — men datasettet hentes på nytt, og den
                      dagen en ladeoperatør får med siden sin, skal en død
                      adresse ikke være enden på veien.
                    */}
                    <a
                      className="amenity-name"
                      href={links.primary.href}
                      target="_blank"
                      rel="noreferrer"
                      title={`${item.name} — ${describe(item, lang) || t[item.kind]}`}
                    >
                      {item.name}
                      <ExternalLink size={10} />
                    </a>
                    <PlaceAltLink link={links.fallback} lang={lang} />
                    <span className="amenity-detail">{describe(item, lang)}</span>
                    <span className="amenity-distance">{item.distanceKm} km</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** Én linje som sier det som faktisk avgjør om stedet er brukbart. */
function describe(amenity: Amenity, lang: UiLang): string {
  const t = dictionaries[lang];
  const parts: string[] = [];

  if (amenity.brand) parts.push(amenity.brand);

  if (amenity.kind === "charging") {
    if (amenity.powerKw) parts.push(`${amenity.powerKw} kW`);
    if (amenity.capacity) parts.push(t.chargePoints(amenity.capacity));
    if (amenity.fastCharge) parts.push(t.fastCharge);
  }

  if (amenity.kind === "fuel" && amenity.diesel) parts.push(t.diesel);

  if (amenity.kind === "food") {
    if (amenity.foodType) parts.push(t.foodType[amenity.foodType]);
    if (amenity.cuisine) parts.push(amenity.cuisine.replace(/_/g, " "));
  }

  return parts.join(" · ");
}
